const BASE_URL = '/api';

function getToken(): string | null {
  return localStorage.getItem('occiput_token');
}

export function setToken(token: string): void {
  localStorage.setItem('occiput_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('occiput_token');
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth = false, ...init } = options;
  const headers = new Headers(init.headers ?? {});

  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!skipAuth) {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as unknown as T;

  const isJson = (response.headers.get('content-type') ?? '').includes('application/json');
  const body = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(isJson ? body.message || body.error || `HTTP ${response.status}` : `HTTP ${response.status}`, response.status, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestInit) => apiFetch<T>(path, { method: 'GET', ...opts }),
  post: <T>(path: string, data?: unknown, opts?: RequestInit & { skipAuth?: boolean }) =>
    apiFetch<T>(path, { method: 'POST', body: data !== undefined ? JSON.stringify(data) : undefined, ...opts }),
  put: <T>(path: string, data?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, { method: 'PUT', body: data !== undefined ? JSON.stringify(data) : undefined, ...opts }),
  patch: <T>(path: string, data?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, { method: 'PATCH', body: data !== undefined ? JSON.stringify(data) : undefined, ...opts }),
  delete: <T>(path: string, opts?: RequestInit) => apiFetch<T>(path, { method: 'DELETE', ...opts }),
};

// --- Named convenience functions ---

export const login = (email: string, password: string) =>
  api.post<{ ok: boolean; session: string; secret: string }>('/auth/login', { email, password }, { skipAuth: true });

export const register = (data: { email: string; password: string; full_name: string; desired_site_name: string; motivation?: string }) =>
  api.post('/auth/register', data, { skipAuth: true });

export const getMe = () =>
  api.get<{ user: { id: string; email: string }; platformUser: { id: string; email: string; role: string; full_name: string } }>('/auth/me');

export const listTenants = () => api.get<unknown[]>('/tenants');

export const getTenant = (id: string) => api.get<unknown>(`/tenants/${id}`);

export const suspendTenant = (id: string) => api.post(`/tenants/${id}/suspend`);

export const reactivateTenant = (id: string) => api.post(`/tenants/${id}/reactivate`);

export const archiveTenant = (id: string) => api.post(`/tenants/${id}/archive`);

export const listWaitlist = () => api.get<unknown[]>('/waitlist');

export const approveWaitlist = (id: string) => api.post(`/waitlist/${id}/approve`);

export const rejectWaitlist = (id: string) => api.post(`/waitlist/${id}/reject`);

export const getSettings = () => api.get<{ platform_name: string; support_email: string; default_tier: string }>('/settings');

export const updateSettings = (data: { platform_name: string; support_email: string; default_tier: string }) =>
  api.put('/settings', data);

export const submitWaitlist = (data: { name: string; email: string; portfolio_url?: string; message?: string }) =>
  api.post('/waitlist', data, { skipAuth: true });

// --- Backups ---

export const listBackups = () => api.get<{ backups: unknown[] }>('/backups');

export const listTenantBackups = (tenantId: string) => api.get<{ backups: unknown[] }>(`/backups/${tenantId}`);

export const createBackup = (tenantId: string) => api.post<{ backupJob: unknown }>(`/backups/${tenantId}`);

export const restoreBackup = (tenantId: string, backupId: string) =>
  api.post(`/backups/${tenantId}/restore/${backupId}`);

// --- Monitoring ---

export const getMonitoringSnapshots = () => api.get<{ snapshots: unknown[] }>('/monitoring/snapshots');

export const getTenantSnapshots = (tenantId: string) => api.get<{ snapshots: unknown[] }>(`/monitoring/snapshots/${tenantId}`);

export const getSystemHealth = () => api.get<{ health: { api: boolean; db: boolean; storage: boolean; details: Record<string, unknown> } }>('/monitoring/health');

export const triggerMonitoringCheck = () => api.post<{ succeeded: number; failed: number; total: number }>('/monitoring/check');

export const clearMonitoringSnapshots = () => api.delete<{ deleted: number }>('/monitoring/snapshots');

export const clearEmailLog = () => api.delete<{ deleted: number }>('/email/log');

export const clearBackupJobs = () => api.delete<{ deleted: number }>('/backups');

// --- Provisioning ---

export async function testTenantConnection(data: { appwrite_project_id: string; appwrite_api_key: string }) {
  return api.post<{ ok: boolean }>('/tenants/setup-test', data);
}

export interface ProjectCheckResult {
  database_exists: boolean;
  existing_collections: string[];
  existing_buckets: string[];
  has_documents: boolean;
  document_counts: Record<string, number>;
  has_users: boolean;
  user_count: number;
}

export async function checkTenantProject(data: { appwrite_project_id: string; appwrite_api_key: string }) {
  return api.post<ProjectCheckResult>('/tenants/check-project', data);
}

export async function provisionTenantManual(data: {
  waitlist_user_id?: string;
  owner_email?: string;
  owner_name?: string;
  name: string;
  slug: string;
  appwrite_project_id?: string;
  appwrite_api_key?: string;
  domain: string;
  plan?: string;
  tenant_admin_password: string;
}) {
  return api.post<{ ok: boolean; tenant: any }>('/tenants/provision-manual', data);
}

// --- Effects ---

import type { AccentEffect } from '../../../shared/types';

export const effectsApi = {
  list: () => api.get<{ effects: AccentEffect[] }>('/effects'),
  generate: (prompt: string, prevCode?: string) =>
    api.post<{ effect: AccentEffect }>('/effects/generate', { prompt, prevCode }),
  convert: (html: string) => api.post<{ effect: AccentEffect }>('/effects/convert', { html }),
  publish: (effect: AccentEffect) => api.post<{ effect: unknown }>('/effects', effect),
  update: (id: string, patch: Partial<AccentEffect>) =>
    api.put<{ effect: unknown }>(`/effects/${id}`, patch),
  remove: (id: string) => api.delete<void>(`/effects/${id}`),
  sync: () => api.post<{ ok: number; failed: number }>('/effects/sync'),
};
